import React from 'react';
import { ShieldCheck, UserX, BadgeCheck, Eye, Building2, Heart, AlertTriangle, CheckCircle, XCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';

const AboutPage = () => {
  const scrollToForm = () => {
    window.location.href = '/#lead-form';
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      {/* Hero Section */}
      <section className="pt-32 pb-16 bg-gradient-to-br from-slate-50 to-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <div className="inline-flex items-center gap-2 bg-[#E0F2FE] text-[#0EA5E9] px-4 py-2 rounded-full text-sm font-medium mb-6">
            <ShieldCheck className="w-4 h-4" />
            <span>Consumer Protection First</span>
          </div>
          
          <h1 
            className="text-4xl md:text-5xl font-bold text-[#0F172A] mb-6"
            style={{ fontFamily: 'Outfit, sans-serif' }}
          >
            Why NOLA HVAC Connect Exists
          </h1>
          
          <p className="text-xl text-slate-600 leading-relaxed max-w-3xl mx-auto">
            We're not another lead generation company. We're industry insiders on a mission to protect 
            New Orleans homeowners from predatory HVAC practices.
          </p>
        </div>
      </section>

      {/* The Problem Section */}
      <section className="py-16 bg-[#0F172A] text-white">
        <div className="max-w-4xl mx-auto px-6">
          <div className="flex items-center gap-3 mb-6">
            <AlertTriangle className="w-8 h-8 text-[#F97316]" />
            <h2 
              className="text-3xl font-bold"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              The Problem We're Fighting
            </h2>
          </div>
          
          <div className="space-y-6 text-slate-300 text-lg leading-relaxed">
            <p>
              Over the past decade, <strong className="text-white">private equity firms have quietly bought up 
              HVAC companies</strong> across Greater New Orleans. From the outside, these companies still look local. 
              Same trucks. Same logos. Same phone numbers.
            </p>
            
            <p>
              But inside? <strong className="text-white">Everything has changed.</strong>
            </p>
            
            <p>
              Technicians are now pressured to hit sales quotas. A simple repair becomes a $15,000 system replacement. 
              "Diagnostic fees" appear out of nowhere. Financing plans with hidden interest rates get pushed on 
              families who just need their AC fixed.
            </p>
            
            <p className="text-[#F97316] font-semibold">
              We watched this happen to our community. We decided to do something about it.
            </p>
          </div>
        </div>
      </section>

      {/* Our Story Section */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 
                className="text-3xl font-bold text-[#0F172A] mb-6"
                style={{ fontFamily: 'Outfit, sans-serif' }}
              >
                An Insider's Perspective
              </h2>
              
              <div className="space-y-4 text-slate-600 leading-relaxed">
                <p>
                  With <strong className="text-[#0F172A]">over a decade in the HVAC industry</strong>, 
                  I've seen it from every angle—as a technician, as a business owner, and as a homeowner.
                </p>
                
                <p>
                  I've watched good companies get bought out and transformed into sales machines. 
                  I've seen honest technicians quit because they refused to upsell unnecessary equipment. 
                  I've heard the horror stories from neighbors who got charged $8,000 for a $200 capacitor.
                </p>
                
                <p>
                  <strong className="text-[#0F172A]">NOLA HVAC Connect is my answer.</strong> A way to use 
                  my insider knowledge to protect the community I love from the industry I know too well.
                </p>
              </div>
            </div>
            
            <div className="bg-slate-50 p-8 rounded-2xl">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-16 h-16 bg-[#0EA5E9] rounded-full flex items-center justify-center">
                  <span className="text-white text-2xl font-bold">10+</span>
                </div>
                <div>
                  <p className="text-2xl font-bold text-[#0F172A]">Years in HVAC</p>
                  <p className="text-slate-500">Industry Experience</p>
                </div>
              </div>
              
              <ul className="space-y-3">
                <li className="flex items-center gap-3 text-slate-700">
                  <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                  <span>Licensed HVAC Professional</span>
                </li>
                <li className="flex items-center gap-3 text-slate-700">
                  <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                  <span>New Orleans Native</span>
                </li>
                <li className="flex items-center gap-3 text-slate-700">
                  <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                  <span>Homeowner in the Community</span>
                </li>
                <li className="flex items-center gap-3 text-slate-700">
                  <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                  <span>Consumer Advocate</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* How We Vet Section */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 
              className="text-3xl font-bold text-[#0F172A] mb-4"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              How We Vet Contractors
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              Not everyone gets to be in our network. We use industry knowledge to separate 
              the honest contractors from the sales operations.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {/* What We Check */}
            <div className="bg-white p-8 rounded-2xl border border-slate-200">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                  <BadgeCheck className="w-6 h-6 text-green-600" />
                </div>
                <h3 className="text-xl font-bold text-[#0F172A]">What We Verify</h3>
              </div>
              
              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-semibold text-[#0F172A]">Active State License</p>
                    <p className="text-sm text-slate-500">Verified with Louisiana licensing board</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-semibold text-[#0F172A]">Liability Insurance</p>
                    <p className="text-sm text-slate-500">Protects you if something goes wrong</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-semibold text-[#0F172A]">Review History</p>
                    <p className="text-sm text-slate-500">Pattern analysis across multiple platforms</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-semibold text-[#0F172A]">Ownership Structure</p>
                    <p className="text-sm text-slate-500">We dig into who actually owns the company</p>
                  </div>
                </li>
              </ul>
            </div>

            {/* What Disqualifies */}
            <div className="bg-white p-8 rounded-2xl border border-slate-200">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center">
                  <UserX className="w-6 h-6 text-red-600" />
                </div>
                <h3 className="text-xl font-bold text-[#0F172A]">Automatic Disqualifiers</h3>
              </div>
              
              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <XCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-semibold text-[#0F172A]">Private Equity Ownership</p>
                    <p className="text-sm text-slate-500">If a PE firm owns you, you're out</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <XCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-semibold text-[#0F172A]">Commission-Based Techs</p>
                    <p className="text-sm text-slate-500">Techs paid to sell, not to fix</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <XCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-semibold text-[#0F172A]">Pattern of Complaints</p>
                    <p className="text-sm text-slate-500">Recurring issues = recurring problems</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <XCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-semibold text-[#0F172A]">Expired/Suspended License</p>
                    <p className="text-sm text-slate-500">No exceptions, no second chances</p>
                  </div>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Our Promise Section */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-6">
          <div className="bg-gradient-to-br from-[#0F172A] to-slate-800 rounded-3xl p-8 md:p-12 text-white">
            <div className="flex items-center gap-3 mb-6">
              <Heart className="w-8 h-8 text-[#F97316]" />
              <h2 
                className="text-3xl font-bold"
                style={{ fontFamily: 'Outfit, sans-serif' }}
              >
                Our Promise to You
              </h2>
            </div>
            
            <div className="space-y-4 text-slate-300 text-lg mb-8">
              <p>
                We don't offer fancy guarantees or marketing gimmicks. What we offer is simpler and more valuable:
              </p>
              
              <p className="text-white font-semibold text-xl">
                Local trust, backed by industry knowledge.
              </p>
              
              <p>
                Every contractor in our network is someone we'd send to our own mother's house. 
                They're local business owners who live in this community, raise families here, 
                and stake their reputation on every job.
              </p>
              
              <p>
                When you request a quote through NOLA HVAC Connect, you're not getting matched with 
                whoever paid the most for your lead. You're getting connected with contractors we've 
                personally vetted—people who fix problems instead of creating them.
              </p>
            </div>

            <Button
              onClick={scrollToForm}
              className="bg-[#F97316] hover:bg-[#EA580C] text-white font-bold py-4 px-8 rounded-full text-lg"
            >
              Find a Trusted Contractor
            </Button>
          </div>
        </div>
      </section>

      {/* Red Flags Section */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 
              className="text-3xl font-bold text-[#0F172A] mb-4"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              Red Flags to Watch For
            </h2>
            <p className="text-slate-600">
              From an industry insider: warning signs that you're dealing with a sales operation, not a service company.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {[
              {
                flag: '"Your system is too old to repair"',
                reality: 'Most systems can be repaired cost-effectively for 15-20 years',
              },
              {
                flag: '"We can\'t get parts for this model"',
                reality: 'Parts are available for nearly every system made in the last 30 years',
              },
              {
                flag: '"This repair will cost almost as much as a new system"',
                reality: 'Common pressure tactic—always get a second opinion',
              },
              {
                flag: '"Special financing available today only"',
                reality: 'High-pressure sales tactic to prevent you from shopping around',
              },
              {
                flag: '"Free diagnostic with any repair"',
                reality: 'Often means inflated repair costs to cover the "free" service',
              },
              {
                flag: '"Your refrigerant is obsolete"',
                reality: 'R-22 phase-out doesn\'t mean your system needs replacing',
              },
            ].map((item, i) => (
              <div key={i} className="bg-white p-6 rounded-xl border border-slate-200">
                <p className="font-semibold text-red-600 mb-2">🚩 {item.flag}</p>
                <p className="text-slate-600 text-sm"><strong>Reality:</strong> {item.reality}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AboutPage;
